import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import {
  DeleteObjectDialog,
  EditObjectEditor,
} from 'widgets/kyrgyzstan/edit-object-editor'
import type { EditObjectListItem } from 'widgets/kyrgyzstan/edit-object-editor'

import { getRequiredBookingConfirmationValue } from 'entities/kyrgyzstan/lib'
import {
  editObjectSessionStore,
  loadRejectedObjectRevisionByDraftId,
} from 'entities/kyrgyzstan/model'

import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import { KyrgyzstanEditObjectApi } from 'shared/api/kyrgyzstan/edit-object'
import { getApiErrorMessage } from 'shared/lib/errorText'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'

import { ObjectOnReviewDialog } from './ui/object-on-review-dialog'

const KyrgyzstanRejectedObjectEditPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { draftId: sourceDraftId } = useParams<{ draftId: string }>()
  const { closeDialog, openDialog } = useDialogBottomSheet()
  const { showApiErrorDialog } = useApiErrorDialog()
  const [isInitialLoading, setIsInitialLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const currentDraft =
    editObjectSessionStore.getDraftBySourceDraftId(sourceDraftId)
  const draftId = currentDraft?.id ?? null
  const isDirty = editObjectSessionStore.isDirty
  const rejectionReason = currentDraft?.rejection_reason?.trim() || null
  const bookingConfirmed = getRequiredBookingConfirmationValue(
    currentDraft?.booking?.booking_confirmed,
  )

  const objectsSubmittedLocation = {
    pathname: RoutingPaths.kyrgyzstan.objects.path(),
    search: '?status=submitted',
  }

  useEffect(() => {
    if (!sourceDraftId) {
      return
    }

    const abortController = new AbortController()

    const loadDraft = async () => {
      setIsInitialLoading(true)
      setError(null)

      try {
        const draft = await loadRejectedObjectRevisionByDraftId(
          sourceDraftId,
          editObjectSessionStore.sourceDraftId,
          editObjectSessionStore.draftId,
          abortController.signal,
        )

        if (!abortController.signal.aborted) {
          editObjectSessionStore.setRejectedDraft(sourceDraftId, draft)
        }
      } catch (requestError) {
        const errorMessage = getApiErrorMessage(requestError)

        if (abortController.signal.aborted || errorMessage === null) {
          return
        }

        setError(errorMessage)
        showApiErrorDialog(requestError)
      } finally {
        if (!abortController.signal.aborted) {
          setIsInitialLoading(false)
        }
      }
    }

    loadDraft()

    return () => {
      abortController.abort()
    }
  }, [sourceDraftId, showApiErrorDialog])

  const handleItemClick = (item: EditObjectListItem) => {
    if (!sourceDraftId || !draftId || !item.stepId) {
      return
    }

    navigate(
      RoutingPaths.kyrgyzstan.objects.draftRevision.step.path(
        sourceDraftId,
        item.stepId,
      ),
    )
  }

  const handleDeleteClick = () => {
    openDialog({
      child: (
        <DeleteObjectDialog
          onConfirm={handleDeleteConfirm}
          onError={showApiErrorDialog}
        />
      ),
      isDialog: false,
      borderRadius: 20,
      padding: [20, 8, 8],
    })
  }

  const handleDeleteConfirm = async () => {
    if (!sourceDraftId) {
      closeDialog()
      return
    }

    await KyrgyzstanEditObjectApi.deleteDraft(sourceDraftId)
    closeDialog()
    editObjectSessionStore.reset()
    navigate(
      {
        pathname: RoutingPaths.kyrgyzstan.objects.path(),
        search: '?status=cancelled',
      },
      { replace: true },
    )
  }

  const handleReviewDialogConfirm = () => {
    closeDialog()
    editObjectSessionStore.reset()
    navigate(objectsSubmittedLocation, { replace: true })
  }

  const handleSubmitClick = async () => {
    if (!draftId || isSubmitting || !isDirty) {
      return
    }

    setIsSubmitting(true)

    try {
      await KyrgyzstanCreateObjectApi.saveBookingStep(draftId, {
        booking_confirmed: getRequiredBookingConfirmationValue(
          currentDraft?.booking?.booking_confirmed,
        ),
      })
      await KyrgyzstanEditObjectApi.submitDraftRevision(draftId)
      openDialog({
        child: <ObjectOnReviewDialog onConfirm={handleReviewDialogConfirm} />,
        isDialog: false,
        borderRadius: 20,
        padding: [20, 8, 8],
      })
    } catch (requestError) {
      showApiErrorDialog(requestError)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <EditObjectEditor
      actionLabel={t('kyrgyzstan_block.edit_object.actions.submit_for_review')}
      bookingConfirmed={bookingConfirmed}
      draft={currentDraft}
      error={error}
      isDirty={isDirty}
      isInitialLoading={isInitialLoading}
      isSubmitting={isSubmitting}
      rejectionReason={rejectionReason}
      onBack={() => navigate(objectsSubmittedLocation)}
      onDelete={handleDeleteClick}
      onItemClick={handleItemClick}
      onSubmit={handleSubmitClick}
    />
  )
})

export default KyrgyzstanRejectedObjectEditPage
